import {
  comprimento,
  cross,
  dot,
  limitar,
  multiplicar,
  normalizar,
  projetarNoPlano,
  radParaGraus,
  subtrair,
  type Vetor3,
} from './vetor3'

export type LadoRotacao = 'esquerdo' | 'direito'

export interface EixoColuna {
  baseColuna: Vetor3
  topoColuna: Vetor3
  frenteVitima: Vetor3
}

export interface ResultadoAngulo {
  /**
   * Rotação no plano transversal, entre o eixo sagital e a saída. É o número da
   * convenção para a vítima SENTADA. Nulo em retirada axial.
   */
  anguloGraus: number | null
  lado: LadoRotacao | null
  rotaAxial: boolean
  /**
   * Desvio entre a direção do puxão e o eixo do corpo: 0° = puxão exatamente
   * ao longo da coluna, 90° = puxão perpendicular a ela.
   *
   * Existe porque o ângulo transversal só informa quando o puxão é
   * perpendicular ao corpo. Com a vítima deitada no teto do veículo capotado o
   * puxão corre ao longo da coluna, e ali é ESTE o número que discrimina as
   * saídas — o transversal fica preso em 90° para todas elas.
   */
  desvioDoEixoGraus: number
  eixoColuna: Vetor3
  eixoSagital: Vetor3 | null
  direcaoRota: Vetor3 | null
}

const TOLERANCIA_LADO_INDEFINIDO = 1e-3

/**
 * Abaixo deste desvio o puxão é considerado axial: o corpo sai ao longo do
 * próprio eixo e não há rotação da coluna cervical a medir.
 *
 * 15° não é medida: é o ponto em que a convenção de trabalho (0/30/60/90)
 * arredonda para 0°. Usar o mesmo corte da convenção evita um segundo número
 * mágico dizendo coisa parecida.
 */
export const LIMIAR_AXIAL_GRAUS = 15

/**
 * Quanto giro a rota impõe à vítima.
 *
 * Um dos princípios da técnica é evitar girar a vítima: quanto maior o ângulo
 * entre o eixo dela e a saída, mais rotação a equipe executa sobre uma coluna
 * que pode estar lesionada. O projeto já classificava o ângulo em 0/30/60/90 —
 * uma CONVENÇÃO DE TRABALHO, sem juízo de gravidade —, e não tinha como dizer
 * que 90° custa mais do que 30°.
 *
 * Os limiares não são números novos: são as mesmas fronteiras de arredondamento
 * que `anguloConvencional` já usa, e o piso é o `LIMIAR_AXIAL_GRAUS` que já
 * decide o que é retirada axial. Uma calibração a menos para o instrutor
 * confirmar.
 *
 * Como `LIMIAR_AXIAL_GRAUS` e os limiares cervicais, é convenção didática
 * PENDENTE DE CONFIRMAÇÃO DO INSTRUTOR, e a UI diz isso.
 */
export type FaixaGiro = 'axial' | 'leve' | 'moderado' | 'acentuado'

/** Fronteira entre giro leve e moderado: onde a convenção passa de 30° a 60°. */
export const LIMIAR_GIRO_MODERADO_GRAUS = 45
/** Fronteira entre moderado e acentuado: onde a convenção passa de 60° a 90°. */
export const LIMIAR_GIRO_ACENTUADO_GRAUS = 75

export function faixaDoGiro(graus: number): FaixaGiro {
  const absoluto = Math.abs(graus)
  if (absoluto < LIMIAR_AXIAL_GRAUS) return 'axial'
  if (absoluto <= LIMIAR_GIRO_MODERADO_GRAUS) return 'leve'
  if (absoluto <= LIMIAR_GIRO_ACENTUADO_GRAUS) return 'moderado'
  return 'acentuado'
}

export interface ReferenciaisColuna {
  /** eixo da coluna normalizado, da base para o topo */
  eixo: Vetor3
  /**
   * eixo sagital projetado no plano transversal: aponta para as costas da
   * vítima, que é a referência de 0° da rotação de retirada.
   */
  eixoSagital: Vetor3
}

/**
 * Referenciais derivados do eixo da coluna.
 *
 * Exposto para que a cena 3D construa rotas e arcos a partir dos MESMOS
 * vetores usados no cálculo do ângulo, em vez de recalcular por conta própria.
 */
export function referenciaisDaColuna(eixoColuna: EixoColuna): ReferenciaisColuna {
  const eixoBruto = subtrair(eixoColuna.topoColuna, eixoColuna.baseColuna)
  if (comprimento(eixoBruto) < 1e-9) {
    throw new Error('topoColuna e baseColuna não podem coincidir.')
  }

  const eixo = normalizar(eixoBruto)
  const frenteProjetada = projetarNoPlano(multiplicar(eixoColuna.frenteVitima, -1), eixo)
  if (comprimento(frenteProjetada) < 1e-6) {
    throw new Error('frenteVitima é nula ou paralela ao eixo da coluna.')
  }

  return { eixo, eixoSagital: normalizar(frenteProjetada) }
}

/**
 * Rotação do corpo, no plano transversal da vítima, entre o eixo sagital e
 * a via de saída. É uma função pura e não depende de React/Three.js.
 *
 * A definição continua marcada como conteúdo doutrinário pendente de
 * confirmação do instrutor. A migração para React não altera a matemática.
 */
export function calcularAnguloExtracao(
  eixoColuna: EixoColuna,
  pontoSaida: Vetor3,
  limiarAxialGraus = LIMIAR_AXIAL_GRAUS,
): ResultadoAngulo {
  const { eixo, eixoSagital } = referenciaisDaColuna(eixoColuna)
  const rumo = subtrair(pontoSaida, eixoColuna.baseColuna)
  if (comprimento(rumo) < 1e-9) {
    throw new Error('pontoSaida coincide com a base da coluna: não há direção de retirada.')
  }

  // desvio em relação ao eixo do corpo. O módulo do cosseno trata puxar pela
  // cabeça e puxar pelos pés como o mesmo caso: ambos são axiais.
  const desvioDoEixoGraus = radParaGraus(
    Math.acos(limitar(Math.abs(dot(eixo, normalizar(rumo))), 0, 1)),
  )

  const direcaoRotaBruta = projetarNoPlano(rumo, eixo)

  // critério angular, não de comprimento: o desvio não depende de quão longe
  // fica o ponto de saída, e é isso que torna a retirada axial detectável.
  if (desvioDoEixoGraus < limiarAxialGraus || comprimento(direcaoRotaBruta) < 1e-6) {
    return {
      anguloGraus: null,
      lado: null,
      rotaAxial: true,
      desvioDoEixoGraus,
      eixoColuna: eixo,
      eixoSagital: null,
      direcaoRota: null,
    }
  }

  const direcaoRota = normalizar(direcaoRotaBruta)
  const cosseno = limitar(dot(eixoSagital, direcaoRota), -1, 1)
  const anguloGraus = radParaGraus(Math.acos(cosseno))
  const sinal = dot(cross(direcaoRota, eixoSagital), eixo)
  const lado = Math.abs(sinal) < TOLERANCIA_LADO_INDEFINIDO
    ? null
    : sinal > 0
      ? 'esquerdo'
      : 'direito'

  return {
    anguloGraus,
    lado,
    rotaAxial: false,
    desvioDoEixoGraus,
    eixoColuna: eixo,
    eixoSagital,
    direcaoRota,
  }
}

/**
 * Decomposição do desvio cervical em três planos anatômicos, para a seta
 * interativa da extração (P3.2).
 *
 * `flexaoExtensaoGraus` e `inclinacaoLateralGraus` decompõem `desvioTotalGraus`
 * nos componentes sagital e frontal (por construção, o quadrado de um mais o
 * quadrado do outro é sempre igual ao quadrado do desvio total — é a mesma
 * conta de decompor um vetor em cosseno/seno do seu próprio azimute).
 * `rotacaoGraus` é a mesma convenção de `calcularAnguloExtracao` aplicada à
 * DIREÇÃO da seta em vez de a um ponto de saída: reaproveita a leitura de
 * 0/30/60/90 que o aluno já conhece da rota, agora usada para dizer PARA ONDE
 * o desvio aponta, não só QUANTO ele vale.
 */
export interface AngulosCervicais {
  /** flexão (+, queixo ao peito) / extensão (−), no plano sagital */
  flexaoExtensaoGraus: number
  /** inclinação lateral; positivo tomba para o lado esquerdo da vítima */
  inclinacaoLateralGraus: number
  /** para onde o desvio aponta no plano transversal, convenção de `calcularAnguloExtracao` */
  rotacaoGraus: number
  /** desvio total entre a seta e o eixo neutro da coluna */
  desvioTotalGraus: number
  faixa: 'alinhado' | 'atencao' | 'excessivo'
}

/**
 * Faixas de leitura do desvio cervical.
 *
 * Convenção didática pendente de confirmação do instrutor, no mesmo espírito
 * de `LIMIAR_AXIAL_GRAUS`: não é limiar clínico, é o corte que a maquete usa
 * para colorir a leitura em verde/âmbar/vermelho.
 */
export const LIMIAR_CERVICAL_ALINHADO_GRAUS = 15
export const LIMIAR_CERVICAL_ATENCAO_GRAUS = 30

function faixaDoDesvioCervical(desvioGraus: number): AngulosCervicais['faixa'] {
  if (desvioGraus <= LIMIAR_CERVICAL_ALINHADO_GRAUS) return 'alinhado'
  if (desvioGraus <= LIMIAR_CERVICAL_ATENCAO_GRAUS) return 'atencao'
  return 'excessivo'
}

/**
 * Decompõe a direção `direcao` (a seta, apontando a partir da base da coluna)
 * em flexão/extensão, inclinação lateral, rotação transversal e desvio total,
 * tendo o eixo da coluna como referência.
 */
export function decomporAngulosCervicais(eixoColuna: EixoColuna, direcao: Vetor3): AngulosCervicais {
  const { eixo, eixoSagital } = referenciaisDaColuna(eixoColuna)
  const d = normalizar(direcao)
  // aponta para o lado esquerdo da vítima (+X)
  const lateralEsquerda = cross(eixoSagital, eixo)

  const desvioTotalGraus = radParaGraus(Math.acos(limitar(dot(d, eixo), -1, 1)))

  const projecaoTransversa = projetarNoPlano(d, eixo)
  if (comprimento(projecaoTransversa) < 1e-6) {
    return {
      flexaoExtensaoGraus: 0,
      inclinacaoLateralGraus: 0,
      rotacaoGraus: 0,
      desvioTotalGraus,
      faixa: faixaDoDesvioCervical(desvioTotalGraus),
    }
  }

  const direcaoTransversa = normalizar(projecaoTransversa)
  // componentes cosseno/seno do azimute: escalados pelo desvio total, dão a
  // decomposição sagital/frontal do MESMO ângulo, não uma medida independente
  const componenteFrente = dot(direcaoTransversa, multiplicar(eixoSagital, -1))
  const componenteEsquerda = dot(direcaoTransversa, lateralEsquerda)

  const anguloRotacao = radParaGraus(Math.acos(limitar(dot(eixoSagital, direcaoTransversa), -1, 1)))
  const sinalRotacao = dot(cross(direcaoTransversa, eixoSagital), eixo) >= 0 ? 1 : -1

  return {
    flexaoExtensaoGraus: desvioTotalGraus * componenteFrente,
    inclinacaoLateralGraus: desvioTotalGraus * componenteEsquerda,
    rotacaoGraus: anguloRotacao * sinalRotacao,
    desvioTotalGraus,
    faixa: faixaDoDesvioCervical(desvioTotalGraus),
  }
}
